import {
	type FlowStateChangedDetail,
	HOST_EVENT_KEYS,
	type HostActionRequestedDetail,
	type InstanceFlowStateChangedDetail,
	type InstanceViewHostSnapshotsChangedDetail,
	isFlowStateChangedDetail,
	isHandleInvalidatedDetail,
	isHostActionRequestedDetail,
	isInstanceFlowStateChangedDetail,
	isInstanceViewHostSnapshotsChangedDetail,
	isNavigationOutcome,
	isNavigationRequestedDetail,
	isOpenRequestedDetail,
	type NavigationOutcome,
	type NavigationRequestedDetail,
	type OpenRequestedDetail,
	toHostEventName,
} from "@getuserfeedback/protocol/host";
import { subscribeHostCustomEvent } from "./host-event-listener";

type Unsubscribe = () => void;

const withAbortSignal = (
	unsubscribe: Unsubscribe,
	signal?: AbortSignal,
): Unsubscribe => {
	if (!signal) {
		return unsubscribe;
	}
	if (signal.aborted) {
		unsubscribe();
		return unsubscribe;
	}
	const onAbort = () => {
		unsubscribe();
	};
	signal.addEventListener("abort", onAbort, { once: true });
	return () => {
		signal.removeEventListener("abort", onAbort);
		unsubscribe();
	};
};

export type ClientHostEventHub = {
	subscribeFlowHandleInvalidation: (
		listener: (flowHandleId: string) => void,
	) => Unsubscribe;
	subscribeFlowState: (
		listener: (detail: FlowStateChangedDetail) => void,
	) => Unsubscribe;
	subscribeFlowStateForHandle: (
		flowHandleId: string,
		listener: (detail: FlowStateChangedDetail) => void,
		options?: { signal?: AbortSignal },
	) => Unsubscribe;
	subscribeInstanceFlowState: (
		listener: (detail: InstanceFlowStateChangedDetail) => void,
		options?: { signal?: AbortSignal },
	) => Unsubscribe;
	subscribeViewHostSnapshots: (
		listener: (detail: InstanceViewHostSnapshotsChangedDetail) => void,
		options?: { signal?: AbortSignal },
	) => Unsubscribe;
	subscribeOpenRequested: (
		listener: (
			detail: OpenRequestedDetail,
			hostEvent: CustomEvent<unknown>,
		) => void,
	) => Unsubscribe;
	subscribeHostActionRequested: (
		listener: (
			detail: HostActionRequestedDetail,
			hostEvent: CustomEvent<unknown>,
		) => void,
	) => Unsubscribe;
	dispose: () => void;
};

export const createClientHostEventHub = (input: {
	instanceId: string;
	onNavigationRequested?: (
		detail: NavigationRequestedDetail,
	) => NavigationOutcome;
	windowRef: Window | null;
}): ClientHostEventHub => {
	if (
		!input.windowRef ||
		typeof input.windowRef.addEventListener !== "function" ||
		typeof input.windowRef.removeEventListener !== "function"
	) {
		const noOp = () => {};
		return {
			subscribeFlowHandleInvalidation: () => noOp,
			subscribeFlowState: () => noOp,
			subscribeFlowStateForHandle: () => noOp,
			subscribeInstanceFlowState: () => noOp,
			subscribeViewHostSnapshots: () => noOp,
			subscribeOpenRequested: () => noOp,
			subscribeHostActionRequested: () => noOp,
			dispose: noOp,
		};
	}

	const flowInvalidationSubscribers = new Set<(flowHandleId: string) => void>();
	const flowStateSubscribersByHandle = new Map<
		string,
		Set<(detail: FlowStateChangedDetail) => void>
	>();
	const flowStateSubscribers = new Set<
		(detail: FlowStateChangedDetail) => void
	>();
	const instanceFlowStateSubscribers = new Set<
		(detail: InstanceFlowStateChangedDetail) => void
	>();
	const viewHostSnapshotSubscribers = new Set<
		(detail: InstanceViewHostSnapshotsChangedDetail) => void
	>();
	const openRequestedSubscribers = new Set<
		(detail: OpenRequestedDetail, hostEvent: CustomEvent<unknown>) => void
	>();
	const hostActionRequestedSubscribers = new Set<
		(detail: HostActionRequestedDetail, hostEvent: CustomEvent<unknown>) => void
	>();

	const offHandleInvalidated = subscribeHostCustomEvent<unknown>({
		eventName: toHostEventName(HOST_EVENT_KEYS.INSTANCE_HANDLE_INVALIDATED),
		windowRef: input.windowRef,
		onEvent: (detail) => {
			if (!isHandleInvalidatedDetail(detail)) {
				return;
			}
			if (
				detail.handleKind !== "flow" ||
				detail.instanceId !== input.instanceId
			) {
				return;
			}
			for (const subscriber of flowInvalidationSubscribers) {
				subscriber(detail.handleId);
			}
		},
	});

	const offFlowState = subscribeHostCustomEvent<unknown>({
		eventName: toHostEventName(HOST_EVENT_KEYS.INSTANCE_FLOW_STATE_CHANGED),
		windowRef: input.windowRef,
		onEvent: (detail) => {
			if (!isFlowStateChangedDetail(detail)) {
				return;
			}
			if (detail.instanceId === input.instanceId) {
				for (const subscriber of flowStateSubscribers) {
					subscriber(detail);
				}
			}
			const subscribers = flowStateSubscribersByHandle.get(detail.flowHandleId);
			if (!subscribers || subscribers.size === 0) {
				return;
			}
			for (const subscriber of subscribers) {
				subscriber(detail);
			}
		},
	});

	const offInstanceFlowState = subscribeHostCustomEvent<unknown>({
		eventName: toHostEventName(
			HOST_EVENT_KEYS.INSTANCE_FLOW_STATE_CHANGED_INSTANCE,
		),
		windowRef: input.windowRef,
		onEvent: (detail) => {
			if (!isInstanceFlowStateChangedDetail(detail)) {
				return;
			}
			if (detail.instanceId !== input.instanceId) {
				return;
			}
			for (const subscriber of instanceFlowStateSubscribers) {
				subscriber(detail);
			}
		},
	});

	const offOpenRequested = subscribeHostCustomEvent<unknown>({
		eventName: toHostEventName(HOST_EVENT_KEYS.INSTANCE_OPEN_REQUESTED),
		windowRef: input.windowRef,
		onEvent: (detail, hostEvent) => {
			if (!isOpenRequestedDetail(detail)) {
				return;
			}
			if (detail.instanceId !== input.instanceId) {
				return;
			}
			for (const subscriber of openRequestedSubscribers) {
				subscriber(detail, hostEvent);
			}
		},
	});

	const offNavigationRequested = subscribeHostCustomEvent<unknown>({
		eventName: toHostEventName(HOST_EVENT_KEYS.INSTANCE_NAVIGATION_REQUESTED),
		windowRef: input.windowRef,
		onEvent: (detail, hostEvent) => {
			if (
				!isNavigationRequestedDetail(detail) ||
				detail.instanceId !== input.instanceId
			) {
				return;
			}
			try {
				const outcome = input.onNavigationRequested?.(detail) ?? "unhandled";
				if (!isNavigationOutcome(outcome)) {
					throw new TypeError("Invalid navigation outcome");
				}
				detail.settle?.(outcome);
				if (outcome !== "unhandled") {
					hostEvent.preventDefault();
				}
			} catch (error) {
				try {
					detail.settle?.("failed");
				} catch {
					// A malformed settlement callback cannot restore browser fallback.
				}
				console.error(
					"Registered navigation handler failed; suppressing browser fallback.",
					error,
				);
				hostEvent.preventDefault();
			}
		},
	});
	const offHostActionRequested = subscribeHostCustomEvent<unknown>({
		eventName: toHostEventName(HOST_EVENT_KEYS.INSTANCE_HOST_ACTION_REQUESTED),
		windowRef: input.windowRef,
		onEvent: (detail, hostEvent) => {
			if (
				!isHostActionRequestedDetail(detail) ||
				detail.instanceId !== input.instanceId
			)
				return;
			for (const subscriber of hostActionRequestedSubscribers)
				subscriber(detail, hostEvent);
		},
	});

	const offViewHostSnapshots = subscribeHostCustomEvent<unknown>({
		eventName: toHostEventName(
			HOST_EVENT_KEYS.INSTANCE_VIEW_HOST_SNAPSHOTS_CHANGED,
		),
		windowRef: input.windowRef,
		onEvent: (detail) => {
			if (
				!isInstanceViewHostSnapshotsChangedDetail(detail) ||
				detail.instanceId !== input.instanceId
			) {
				return;
			}
			for (const subscriber of viewHostSnapshotSubscribers) {
				subscriber(detail);
			}
		},
	});

	const dispose = () => {
		offHandleInvalidated();
		offFlowState();
		offInstanceFlowState();
		offOpenRequested();
		offNavigationRequested();
		offHostActionRequested();
		offViewHostSnapshots();
		flowInvalidationSubscribers.clear();
		flowStateSubscribers.clear();
		flowStateSubscribersByHandle.clear();
		instanceFlowStateSubscribers.clear();
		openRequestedSubscribers.clear();
		hostActionRequestedSubscribers.clear();
		viewHostSnapshotSubscribers.clear();
	};

	return {
		subscribeFlowHandleInvalidation: (listener) => {
			flowInvalidationSubscribers.add(listener);
			return () => {
				flowInvalidationSubscribers.delete(listener);
			};
		},
		subscribeFlowState: (listener) => {
			flowStateSubscribers.add(listener);
			return () => {
				flowStateSubscribers.delete(listener);
			};
		},
		subscribeFlowStateForHandle: (flowHandleId, listener, options) => {
			const subscribers = flowStateSubscribersByHandle.get(flowHandleId);
			if (subscribers) {
				subscribers.add(listener);
			} else {
				flowStateSubscribersByHandle.set(flowHandleId, new Set([listener]));
			}
			return withAbortSignal(() => {
				const current = flowStateSubscribersByHandle.get(flowHandleId);
				if (!current) {
					return;
				}
				current.delete(listener);
				if (current.size === 0) {
					flowStateSubscribersByHandle.delete(flowHandleId);
				}
			}, options?.signal);
		},
		subscribeInstanceFlowState: (listener, options) => {
			instanceFlowStateSubscribers.add(listener);
			return withAbortSignal(() => {
				instanceFlowStateSubscribers.delete(listener);
			}, options?.signal);
		},
		subscribeViewHostSnapshots: (listener, options) => {
			viewHostSnapshotSubscribers.add(listener);
			return withAbortSignal(() => {
				viewHostSnapshotSubscribers.delete(listener);
			}, options?.signal);
		},
		subscribeOpenRequested: (listener) => {
			openRequestedSubscribers.add(listener);
			return () => {
				openRequestedSubscribers.delete(listener);
			};
		},
		subscribeHostActionRequested: (listener) => {
			hostActionRequestedSubscribers.add(listener);
			return () => hostActionRequestedSubscribers.delete(listener);
		},
		dispose,
	};
};
